import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { Button } from './common/Catalyst/button';
import { Checkbox } from './common/Catalyst/checkbox';
import { bankService } from './clients/ApiClients';
import { PayableReturn } from './clients/rust-api-client';
import { formatCurrency, formatDate } from './utils/bankHelpers';
import { apiErrorMessage } from './utils/apiErrors';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import {
  payableDirectionLabel,
  payableStatusColor,
  payableStatusLabel,
  sumAmounts,
} from './utils/payableHelpers';
import LoadingSpinner from './LoadingSpinner';
import PayableSettleDialog from './PayableSettleDialog';
import PayableCancelDialog from './PayableCancelDialog';

interface PayablesPanelProps {
  connectionId: string;
}

/**
 * The settle queue for one connection. Pending items are selectable;
 * **Settle selected** opens the RSA dialog, which is the only thing
 * here that moves money. Cancel asks the bank to drop an item it still
 * holds and reverses whatever it posted.
 */
const PayablesPanel: React.FC<PayablesPanelProps> = ({ connectionId }) => {
  const [items, setItems] = useState<PayableReturn[]>([]);
  const [pending, setPending] = useState(0);
  const [pendingOnly, setPendingOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [settling, setSettling] = useState(false);
  const [cancelling, setCancelling] = useState<PayableReturn | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await bankService.listConnectionPayables({
        connectionId,
        status: pendingOnly ? 'pending' : null,
      });
      setItems(resp.data.items);
      setPending(resp.data.pending);
      setSelected(new Set());
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't load payables."));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [connectionId, pendingOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingItems = useMemo(
    () => items.filter((i) => i.status === 'pending'),
    [items]
  );
  const selectedItems = useMemo(
    () => pendingItems.filter((i) => selected.has(i.id)),
    [pendingItems, selected]
  );
  const allSelected =
    pendingItems.length > 0 && selectedItems.length === pendingItems.length;

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(
      allSelected ? new Set() : new Set(pendingItems.map((i) => i.id))
    );

  // Replace rows in place from the settle's per-item answer, so the
  // dialog still showing the outcome sits over a table that agrees.
  const handleSettled = (after: PayableReturn[]) => {
    setItems((current) => {
      const byId = new Map(after.map((p) => [p.id, p]));
      const next = current.map((i) => byId.get(i.id) ?? i);
      setPending(next.filter((i) => i.status === 'pending').length);
      return next;
    });
    setSelected(new Set());
  };

  const handleCancelled = (after: PayableReturn) => {
    setItems((current) => {
      const next = current.map((i) => (i.id === after.id ? after : i));
      setPending(next.filter((i) => i.status === 'pending').length);
      return next;
    });
    setSelected((current) => {
      const next = new Set(current);
      next.delete(after.id);
      return next;
    });
  };

  return (
    <div className="mt-4">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Text className="text-sm">
          {pending === 0
            ? 'Nothing waiting to settle.'
            : `${pending} ${pending === 1 ? 'item is' : 'items are'} waiting to settle.`}
        </Text>
        <div className="flex items-center gap-2">
          <Button plain onClick={() => setPendingOnly((v) => !v)}>
            {pendingOnly ? 'Show all' : 'Show pending only'}
          </Button>
          <Button plain onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
          <Button
            color="sky"
            onClick={() => setSettling(true)}
            disabled={selectedItems.length === 0}
          >
            {selectedItems.length === 0
              ? 'Settle selected'
              : `Settle ${selectedItems.length} · ${formatCurrency(
                  sumAmounts(selectedItems)
                )}`}
          </Button>
        </div>
      </div>

      <ErrorAlert className="mb-3" onRetry={() => void load()}>
        {error}
      </ErrorAlert>

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <LoadingSpinner />
          ) : items.length === 0 ? (
            <Text>
              {pendingOnly
                ? 'No payables waiting to settle on this connection.'
                : 'No payables on this connection.'}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>
                      {pendingItems.length > 0 && (
                        <Checkbox
                          aria-label="Select all pending"
                          checked={allSelected}
                          onChange={toggleAll}
                        />
                      )}
                    </TableHeader>
                    <TableHeader>Effective</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Account</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader className="text-right">Amount</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader className="text-right">Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((i) => {
                    const isPending = i.status === 'pending';
                    const cancellable =
                      isPending ||
                      (i.status === 'settled' && i.bank_status !== 'Complete');
                    return (
                      <TableRow key={i.id}>
                        <TableCell>
                          {isPending && (
                            <Checkbox
                              aria-label={`Select ${i.memo ?? i.id}`}
                              checked={selected.has(i.id)}
                              onChange={() => toggle(i.id)}
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatDate(i.effective_date)}</TableCell>
                        <TableCell>
                          <Badge
                            color={
                              i.direction === 'collection' ? 'lime' : 'sky'
                            }
                          >
                            {payableDirectionLabel(i)}
                          </Badge>
                          {i.mock_source_url && (
                            <Badge color="purple" className="ml-1">
                              Mock
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {i.account_number}
                        </TableCell>
                        <TableCell>
                          <span>{i.memo ?? '—'}</span>
                          {i.bank_transaction_id && (
                            <Text className="text-xs">
                              Bank #{i.bank_transaction_id}
                            </Text>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(Number(i.amount))}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge color={payableStatusColor(i.status)}>
                              {payableStatusLabel(i)}
                            </Badge>
                            {i.bank_status && i.status !== 'pending' && (
                              <Text className="text-xs">
                                Bank: {i.bank_status}
                              </Text>
                            )}
                            {i.error_message &&
                              (i.status === 'pending' ||
                                i.status === 'failed') && (
                                <Text className="text-xs text-danger">
                                  {i.error_message}
                                </Text>
                              )}
                            {i.status === 'settled' && i.settled_at && (
                              <Text className="text-xs">
                                Settled {formatDate(i.settled_at)}
                              </Text>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {cancellable && (
                            <Button outline onClick={() => setCancelling(i)}>
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
      <Text className="mt-2 text-xs">
        Settling sends the batch to the bank with your one-time RSA code; the
        money moves on the bank’s next processing day. A settled item can still
        be cancelled until the bank starts processing it.
      </Text>

      <PayableSettleDialog
        open={settling}
        items={selectedItems}
        connectionId={connectionId}
        onClose={() => setSettling(false)}
        onSettled={handleSettled}
      />
      <PayableCancelDialog
        item={cancelling}
        onClose={() => setCancelling(null)}
        onCancelled={handleCancelled}
      />
    </div>
  );
};

export default PayablesPanel;
